import React from "react";

// Lightweight shimmer skeleton. Use in place of "Loading..." text on any
// surface where the eventual content has a known footprint — e.g. a card
// stack, a quote summary, a table row.
//
// Two flavours:
//   <Skeleton />            -> single full-width bar (defaults to 14px tall)
//   <SkeletonStack lines/>  -> N stacked bars with varied widths
//
// Styling is self-contained so this component can be dropped into any
// page without having to thread CSS-in-JS or extend styles.css.

const SHIMMER_KEYFRAMES = `
@keyframes rqp-skeleton-shimmer {
  0%   { background-position: -240px 0; }
  100% { background-position: 240px 0; }
}
`;

const baseStyle = {
  display: "block",
  height: "14px",
  borderRadius: "var(--radius-sm, 8px)",
  background:
    "linear-gradient(90deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.06) 80%)",
  backgroundSize: "480px 100%",
  backgroundRepeat: "no-repeat",
  animation: "rqp-skeleton-shimmer 1.4s ease-in-out infinite",
};

/**
 * @param {Object} props
 * @param {number|string} [props.width] - CSS width (e.g. "60%" or 200).
 * @param {number|string} [props.height] - CSS height (defaults to 14px).
 * @param {Object} [props.style] - Extra styles merged onto the bar.
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel] - Defaults to "Loading".
 */
export default function Skeleton({
  width = "100%",
  height,
  style,
  className,
  ariaLabel = "Loading",
}) {
  return (
    <>
      <style>{SHIMMER_KEYFRAMES}</style>
      <span
        role="status"
        aria-label={ariaLabel}
        className={className}
        style={{
          ...baseStyle,
          width,
          ...(height ? { height } : null),
          ...(style || null),
        }}
      />
    </>
  );
}

/**
 * @param {Object} props
 * @param {number} [props.lines=3]
 * @param {Array<string|number>} [props.widths] - Override per-line widths.
 * @param {number|string} [props.gap=10]
 */
export function SkeletonStack({ lines = 3, widths, gap = 10 }) {
  const fallback = ["100%", "85%", "70%"];
  const computed = widths || Array.from({ length: lines }, (_, i) => fallback[i % fallback.length]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {computed.map((w, i) => (
        <Skeleton key={i} width={w} />
      ))}
    </div>
  );
}
